import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Select2 } from '@/components/forms/Select2';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { useTimeZone, useTimezones, useUpdateTimeZone } from './api';

interface TimeZoneTabProps {
  canChange: boolean;
}

// Mirrors the ABP SettingManagement "Time Zone" tab: a single timezone select
// (native <select> bound to GetTimezonesAsync) plus a Save button.
export function TimeZoneTab({ canChange }: TimeZoneTabProps) {
  const toast = useToast();
  const settingQuery = useTimeZone();
  const timezonesQuery = useTimezones();
  const updateMut = useUpdateTimeZone();

  const [timezone, setTimezone] = useState('');

  useEffect(() => {
    if (settingQuery.data) setTimezone(settingQuery.data.timezone);
  }, [settingQuery.data]);

  const options = useMemo(
    () => (timezonesQuery.data ?? []).map((tz) => ({ value: tz.value, label: tz.name })),
    [timezonesQuery.data],
  );

  if (settingQuery.isLoading || timezonesQuery.isLoading) return <Spinner />;
  if (settingQuery.isError || timezonesQuery.isError)
    return (
      <ErrorState
        message="Could not load the time zone setting."
        onRetry={() => {
          void settingQuery.refetch();
          void timezonesQuery.refetch();
        }}
      />
    );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    updateMut.mutate(
      { timezone },
      {
        onSuccess: () => toast.success('Saved successfully.'),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Failed to save the time zone.'),
      },
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <Select2
        label="Timezone"
        value={timezone}
        disabled={!canChange || updateMut.isPending}
        allowClear={false}
        onChange={(v) => setTimezone(v ?? '')}
        options={options}
      />

      {canChange && (
        <button type="submit" className="btn btn-primary mt-3" disabled={updateMut.isPending}>
          {updateMut.isPending ? 'Saving…' : 'Save'}
        </button>
      )}
    </form>
  );
}
